/**
 * Service for calculating Inherent Risk values and derived fields
 */

// PD Score options
export const PD_SCORE_OPTIONS = [1, 2, 3, 4, 5];

// BI Score options
export const BI_SCORE_OPTIONS = [1, 3, 9, 27, 81];

// Likelihood options
export const LIKELIHOOD_OPTIONS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Certain'];

// Consequence levels
export const CONSEQUENCE_LEVELS = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Severe'];

// Inherent Risk levels
export const INHERENT_RISK_LEVELS = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Severe'];

/**
 * Calculate PD*BI value
 */
export const calculatePdBi = (pdScore, biScore) => {
    if (!pdScore || !biScore) return null;
    const pd = parseFloat(pdScore);
    const bi = parseFloat(biScore);
    if (isNaN(pd) || isNaN(bi)) return null;
    return pd * bi;
};

/**
 * Calculate Consequence based on PD*BI value
 */
export const calculateConsequence = (pdBi) => {
    if (pdBi === null || pdBi === undefined || pdBi === '') return '';
    const value = parseFloat(pdBi);
    if (isNaN(value)) return '';

    if (value < 1) return 'Insignificant';
    if (value >= 1 && value < 7) return 'Minor';
    if (value >= 7 && value < 28) return 'Moderate';
    if (value >= 29 && value < 82) return 'Major';
    if (value >= 83 && value < 406) return 'Severe';
    return 'Severe';
};

/**
 * Calculate Inherent Risk Score based on Consequence and Likelihood
 */
export const calculateInherentRiskScore = (consequence, likelihood) => {
    if (!consequence || !likelihood) return null;

    const scoreMap = {
        'Insignificant': {
            'Certain': { score: 6, risk: 'Moderate' },
            'Likely': { score: 7, risk: 'Minor' },
            'Possible': { score: 8, risk: 'Minor' },
            'Unlikely': { score: 9, risk: 'Insignificant' },
            'Rare': { score: 10, risk: 'Insignificant' },
        },
        'Minor': {
            'Certain': { score: 5, risk: 'Moderate' },
            'Likely': { score: 5, risk: 'Moderate' },
            'Possible': { score: 7, risk: 'Minor' },
            'Unlikely': { score: 8, risk: 'Minor' },
            'Rare': { score: 9, risk: 'Insignificant' },
        },
        'Moderate': {
            'Certain': { score: 3, risk: 'Major' },
            'Likely': { score: 4, risk: 'Major' },
            'Possible': { score: 6, risk: 'Moderate' },
            'Unlikely': { score: 7, risk: 'Minor' },
            'Rare': { score: 8, risk: 'Minor' },
        },
        'Major': {
            'Certain': { score: 2, risk: 'Severe' },
            'Likely': { score: 3, risk: 'Major' },
            'Possible': { score: 5, risk: 'Moderate' },
            'Unlikely': { score: 6, risk: 'Moderate' },
            'Rare': { score: 6, risk: 'Moderate' },
        },
        'Severe': {
            'Certain': { score: 1, risk: 'Severe' },
            'Likely': { score: 2, risk: 'Severe' },
            'Possible': { score: 3, risk: 'Major' },
            'Unlikely': { score: 5, risk: 'Moderate' },
            'Rare': { score: 6, risk: 'Moderate' },
        },
    };

    const result = scoreMap[consequence]?.[likelihood];
    return result || null;
};

/**
 * Get color for PD Score
 */
export const getPdScoreColor = (pdScore) => {
    const colors = {
        1: 'bg-[#0000FF]',
        2: 'bg-[#008000]',
        3: 'bg-[#FEF800]',
        4: 'bg-[#F66400]',
        5: 'bg-[#DA0000]',
    };
    return colors[pdScore] || 'bg-gray-500';
};

/**
 * Get color for BI Score
 */
export const getBiScoreColor = (biScore) => {
    const bi = parseFloat(biScore);
    if (isNaN(bi)) return 'bg-gray-500';

    const colors = {
        1: 'bg-[#0000FF]',
        3: 'bg-[#008000]',
        9: 'bg-[#FEF800]',
        27: 'bg-[#F66400]',
        81: 'bg-[#DA0000]',
    };
    return colors[bi] || 'bg-gray-500';
};

/**
 * Calculate all derived values for a row
 */
export const calculateDerivedValues = (row) => {
    const pdBi = calculatePdBi(row.pd, row.bi);
    const consequence = calculateConsequence(pdBi);
    const scoreData = calculateInherentRiskScore(consequence, row.likelihood);

    return {
        pdBi: pdBi !== null ? pdBi.toString() : '',
        consequence,
        inherentRisk: scoreData?.risk || '',
        score: scoreData?.score?.toString() || '',
    };
};

